export type VariableType =
  | "string"
  | "number"
  | "boolean"
  | "date"
  | "url"
  | "email";

export interface TypedVariable {
  key: string;
  label?: string;
  type: VariableType;
  description?: string;
  sample?: any;
  required?: boolean;
}

export const VARIABLE_TYPE_OPTIONS: { value: VariableType; label: string }[] = [
  { value: "string", label: "Text" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "date", label: "Date" },
  { value: "url", label: "URL" },
  { value: "email", label: "Email" },
];

export const isTypedVariable = (v: any): v is TypedVariable =>
  Boolean(v) &&
  typeof v === "object" &&
  typeof v.key === "string" &&
  typeof v.type === "string";

const humanize = (key: string): string =>
  key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const inferType = (key: string): VariableType => {
  const lower = key.toLowerCase();
  if (lower.endsWith("_email") || lower === "email") return "email";
  if (
    lower.endsWith("_link") ||
    lower.endsWith("_url") ||
    lower === "url" ||
    lower.includes("link")
  )
    return "url";
  if (lower.endsWith("_date") || lower === "date") return "date";
  if (lower.endsWith("_count") || lower.endsWith("_amount")) return "number";
  if (lower.startsWith("is_") || lower.startsWith("has_")) return "boolean";
  return "string";
};

export const normalizeVariables = (
  input: unknown,
): TypedVariable[] => {
  if (!input) return [];
  if (!Array.isArray(input)) return [];
  return input
    .map((entry: any) => {
      if (typeof entry === "string") {
        return {
          key: entry,
          label: humanize(entry),
          type: inferType(entry),
        } as TypedVariable;
      }
      if (isTypedVariable(entry)) {
        return {
          ...entry,
          label: entry.label || humanize(entry.key),
        } as TypedVariable;
      }
      return null;
    })
    .filter((v): v is TypedVariable => Boolean(v));
};

export const serializeVariables = (
  vars: TypedVariable[],
): TypedVariable[] | string[] => {
  if (vars.every((v) => v.type === "string" && !v.required && !v.description)) {
    return vars.map((v) => v.key);
  }
  return vars;
};

export const variableKeys = (input: unknown): string[] =>
  normalizeVariables(input).map((v) => v.key);
